/**
 * @file src/world/skyboxFaceUrls.js
 * @description Where the renderer will look for a skybox's faces.
 *
 * Exists so a download can pin the files the scene actually fetches. That sounds obvious and the first
 * attempt got it wrong in an interesting way: the face URLs were derived from the texture catalogue's
 * own `baseUrl` + `basePath` + id, which reads like the right thing — the catalogue is the source of
 * truth about textures — except that the cube-map loader never consults the catalogue for a skybox
 * path. It builds one from constants. The two agree today only because the live catalogue happens to
 * say `…/default` and `/skybox`, and they part company in three ways: a catalogue with no `baseUrl`,
 * the `/skyboxes` spelling, and any id that is not already three digits (`"7"`, `"01_skycube"`) which
 * the loader pads and the catalogue does not.
 *
 * So this mirrors the LOADER, because the loader is what will ask. That makes it a copy of a decision
 * owned by `entangled-worlds-orbiters-shared`, and the copy is deliberate: the loader's own builder
 * lives in a module that imports three.js, and importing it here would pull the whole engine into the
 * downloaded-library page — a separate entry whose entire purpose is to open when the renderer cannot.
 * The right fix is for the shared package to export this decision from a THREE-free module; until it
 * does, this is the one place the duplication lives, and it is named so it can be found and deleted.
 *
 * Mirrors `normalizeSkycubeName` and the face-path construction in the shared package's
 * `loaders/cubeMapLoader.js`. If that file changes, this one is wrong.
 */
import { resolvePublicUrl } from 'entangled-worlds-orbiters-shared/config';

/** The face names a cubemap is built from, in the loader's order. */
export const SKYBOX_FACES = ['right', 'left', 'top', 'bottom', 'front', 'back'];

/** Where every skybox lives, relative to the public base. The loader's constant. */
const SKYBOX_BASE_PATH = '/default/skybox';

/** The suffix the loader appends for a low-detail device. */
const LOW_DETAIL_SUFFIX = '_low';

/**
 * The loader's id normalisation: three digits, from whatever the catalogue said.
 *
 * A download that pinned `/skybox/7/right.ktx2` while the renderer asked for `/skybox/007/right.ktx2`
 * would store six files nobody fetches and still draw no sky.
 *
 * @param {string|null|undefined} raw
 * @returns {string}
 */
export function normalizeSkyboxId(raw) {
  if (!raw) return '001';
  const legacy = String(raw).match(/^(\d{1,2})_skycube(?:_low)?$/);
  if (legacy) return String(Number.parseInt(legacy[1], 10)).padStart(3, '0');
  const digits = String(raw).match(/(\d{1,3})/);
  if (digits) return String(Number.parseInt(digits[1], 10)).padStart(3, '0');
  return '001';
}

/**
 * Every face URL a download needs for one skybox: the standard set and the low-detail set.
 *
 * Both, because the loader chooses between them at PLAY time — from the scene profile or a device
 * heuristic — and neither is knowable when the download runs: the profile can change between
 * downloading and playing, and so can the device. Six extra faces is about 0.6 MB, shared by key
 * across every collection wanting the same sky, and it is the difference between a sky that draws and
 * one that does not.
 *
 * KTX2 only, matching what the renderer prefers. The loader falls back to PNG when a face will not
 * decode, but a download that stored the fallback would store files the scene never asks for.
 *
 * @param {string|null|undefined} skyboxId the catalogue's id
 * @returns {{ id: string, urls: string[] }} the normalised id, and twelve URLs
 */
export function skyboxFaceUrls(skyboxId) {
  const id = normalizeSkyboxId(skyboxId);
  const urls = [];
  for (const suffix of ['', LOW_DETAIL_SUFFIX]) {
    for (const face of SKYBOX_FACES) {
      urls.push(resolvePublicUrl(`${SKYBOX_BASE_PATH}/${id}${suffix}/${face}.ktx2`));
    }
  }
  return { id, urls };
}

/** Which face a URL from {@link skyboxFaceUrls} is, so it can be named in the store. */
export function faceOfUrl(url) {
  const match = String(url).match(/\/([^/]+)\.ktx2$/);
  return match && SKYBOX_FACES.includes(match[1]) ? match[1] : null;
}
